import type { StyleLibrary } from "schemas/generated/project";

import React from "react";

import { useFetchGlobalsCssForWebContainer } from "@editor/hooks/designLibrary/useFetchGlobalsCssForWebContainer";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { stylesRouter, stylesUtils } from "@editor/trpc/styles";

import { useNextDevServer } from "@/features/code-execution/context";
import {
  useCurrentStyleLibrary,
  useSandboxStateActions,
} from "@/features/react-editor/store/SandboxStateStoreContext";
import isEqual from "lodash-es/isEqual";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

type DesignLibraryView =
  | "summary"
  | "colors"
  | "typography"
  | "presets"
  | "fontPresets"
  | "customFonts";

export function useDesignLibrary(projectId: string | null | undefined) {
  const updateGlobalsCss = useFetchGlobalsCssForWebContainer();
  const { setShouldSaveHtmlContent } = useNextDevServer();
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const [currentView, setCurrentView] =
    React.useState<DesignLibraryView>("summary");
  const [previousView, setPreviousView] =
    React.useState<DesignLibraryView>("summary");

  const currentStyleLibrary = useCurrentStyleLibrary();
  const { setCurrentStyleLibrary } = useSandboxStateActions();

  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    { projectId: projectId ?? "" },
    { enabled: Boolean(projectId) },
  );

  const { mutateAsync: updateStyleLibrary } =
    stylesRouter.createOrUpdateStyleLibrary.useMutation();

  const hasUnsavedChanges = !isEqual(
    currentStyleLibrary,
    styleLibraryData?.styleLibrary ?? null,
  );

  const handleStyleLibraryChange = async (
    updatedStyleLibrary: StyleLibrary,
  ) => {
    if (!projectId) {
      return;
    }

    setCurrentStyleLibrary(updatedStyleLibrary);
    void updateGlobalsCss({
      styleLibrary: updatedStyleLibrary,
    });
  };

  const handleDiscard = async () => {
    if (!projectId) {
      return;
    }

    await stylesUtils.getStyleLibrary.invalidate({ projectId });

    const originalStyleLibrary = styleLibraryData?.styleLibrary;
    if (originalStyleLibrary) {
      setCurrentStyleLibrary(originalStyleLibrary);
      void updateGlobalsCss({
        styleLibrary: originalStyleLibrary,
      });
    }
  };

  const handleSave = async () => {
    if (!projectId) {
      return;
    }

    setShouldSaveHtmlContent(true);

    await updateStyleLibrary({
      projectId,
      styleLibrary: currentStyleLibrary,
    });

    await stylesUtils.getStyleLibrary.invalidate({
      projectId: projectId ?? "",
    });

    if (isServerSandboxEnabled) {
      await updateGlobalsCss({
        styleLibrary: currentStyleLibrary ?? undefined,
      });
    }
  };

  const handleCustomFontsClick = () => {
    setPreviousView(currentView);
    setCurrentView("customFonts");
  };

  const handleCustomFontsBack = () => {
    setCurrentView(previousView);
  };

  React.useEffect(() => {
    if (hasUnsavedChanges) {
      setShouldSaveHtmlContent(false);
    }
  }, [setShouldSaveHtmlContent, hasUnsavedChanges]);

  // NOTE (Gabe 2025-09-10): We use this weird callbackRef pattern here because
  // we want to call revertStyleChanges on unmount, but we don't want to trigger
  // excessive cleanup functions be using it as a dependency of the effect which
  // handles the unmount.
  const cleanupOnUnmount = React.useCallback(() => {
    if (hasUnsavedChanges && styleLibraryData?.styleLibrary) {
      void updateGlobalsCss({
        styleLibrary: styleLibraryData.styleLibrary,
      });
    }
    setShouldSaveHtmlContent(true);
  }, [
    hasUnsavedChanges,
    styleLibraryData?.styleLibrary,
    updateGlobalsCss,
    setShouldSaveHtmlContent,
  ]);
  const cleanupOnUnmountRef = React.useRef(cleanupOnUnmount);
  React.useEffect(() => {
    cleanupOnUnmountRef.current = cleanupOnUnmount;
  }, [cleanupOnUnmount]);

  // NOTE (Bobby, 2025-10-08): Sync fetched style library to store when it changes from the server
  // but only if we don't have unsaved local changes
  useOnValueChange(
    styleLibraryData?.styleLibrary ?? null,
    (currentValue) => {
      setCurrentStyleLibrary(currentValue);
    },
    {
      equalityFn: isEqual,
      // NOTE (Bobby, 2025-10-08): Don't skip if currentStyleLibrary is null (initial state) OR if there are no unsaved changes
      shouldSkip: currentStyleLibrary !== null && hasUnsavedChanges,
    },
  );

  React.useEffect(() => {
    return () => {
      cleanupOnUnmountRef.current();
    };
  }, []);

  return {
    // State
    currentView,
    setCurrentView,
    previousView,
    setPreviousView,
    currentStyleLibrary,
    setCurrentStyleLibrary,
    hasUnsavedChanges,
    styleLibraryData,

    // Handlers
    handleStyleLibraryChange,
    handleDiscard,
    handleSave,
    handleCustomFontsClick,
    handleCustomFontsBack,
  };
}
